import { useEffect, useId, useRef } from 'react';

/**
 * Manages Preline HSOverlay open/close state in sync with a React boolean.
 *
 * Handles: programmatic open/close, escape key, and backdrop click.
 * Returns { overlayId, overlayRef } to spread onto the overlay root element.
 *
 * @param {boolean} isOpen - Whether the overlay should be open.
 * @param {function} onClose - Called when the overlay is dismissed (escape / backdrop).
 * @param {string} prefix - ID prefix for the overlay element.
 */
export function useHSOverlay(isOpen, onClose, prefix = 'hs-overlay') {
  const uniqueId = useId();
  const overlayId = `${prefix}-${uniqueId.replace(/:/g, '')}`;
  const overlayRef = useRef(null);
  const isOpenRef = useRef(false);

  // Sync React state -> HSOverlay open/close
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    async function syncOverlay() {
      try {
        const { HSOverlay } = await import('preline/dist/index.js');

        // Ensure Preline has registered this overlay instance.
        // Dynamically-mounted overlays (e.g. inside file tree nodes) miss
        // the initial autoInit() call, so open() silently does nothing.
        if (!HSOverlay.getInstance(el)) {
          new HSOverlay(el);
        }

        if (isOpen && !isOpenRef.current) {
          isOpenRef.current = true;
          HSOverlay.open(el);
        } else if (!isOpen && isOpenRef.current) {
          isOpenRef.current = false;
          HSOverlay.close(el);
        }
      } catch {
        // Preline not available (e.g. unit tests) — toggle visibility manually
        if (isOpen && !isOpenRef.current) {
          isOpenRef.current = true;
          el.classList.remove('hidden');
          el.classList.add('open');
        } else if (!isOpen && isOpenRef.current) {
          isOpenRef.current = false;
          el.classList.add('hidden');
          el.classList.remove('open');
        }
      }
    }
    syncOverlay();
  }, [isOpen]);

  // Listen for HSOverlay-initiated close (escape key, backdrop click)
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    function handleClose() {
      if (isOpenRef.current) {
        isOpenRef.current = false;
        onClose();
      }
    }

    el.addEventListener('close.hs.overlay', handleClose);
    return () => el.removeEventListener('close.hs.overlay', handleClose);
  }, [onClose]);

  return { overlayId, overlayRef };
}
